import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fbt } from 'fbtee'
import { useAuth } from '../hooks/useAuth'
import { ConfirmDialog } from './ConfirmDialog'
import {
  fetchManifest,
  getPreviewChannel,
  isValidChannelId,
  loadPreviewChannel,
  type OtaManifest,
} from '../lib/ota'
import { classifyError } from '../lib/errors'
import { toastError } from '../lib/toast'
import { log } from '../lib/logger'

export const PREVIEW_PARAM = 'preview'

// Mounted app-wide, so `?preview=<channel>` works on any route and the URL it
// rides on is the screen you land in. Loading a bundle is remote code
// execution, so the param carries a channel id resolved against our own Storage
// bucket — never a URL — and only an admin is ever prompted.
export function PreviewLoadPrompt() {
  const [params, setParams] = useSearchParams()
  const { isAdmin, loading: authLoading } = useAuth()
  const [manifest, setManifest] = useState<OtaManifest | null>(null)

  const requested = params.get(PREVIEW_PARAM)
  const pinned = getPreviewChannel()

  useEffect(() => {
    if (!requested || authLoading) return

    function strip() {
      setParams(
        prev => {
          prev.delete(PREVIEW_PARAM)
          return prev
        },
        { replace: true },
      )
    }

    // Already running this channel — this is the restored path after the swap,
    // or a re-shared link. Drop the param instead of prompting again, which is
    // also what stops the restore from looping.
    if (pinned === requested || !isValidChannelId(requested) || !isAdmin) {
      strip()
      return
    }

    let cancelled = false
    fetchManifest(requested)
      .then(result => {
        if (cancelled) return
        if (!result) {
          toastError(
            fbt('Preview not found', 'Toast title when a preview channel has no published bundle'),
            fbt('It may have expired.', 'Toast body when a preview channel has no published bundle'),
          )
          strip()
          return
        }
        setManifest(result)
      })
      .catch(err => {
        if (cancelled) return
        toastError(
          fbt("Couldn't load that preview", 'Toast title when fetching a preview build manifest fails'),
          classifyError(err),
        )
        strip()
      })

    return () => {
      cancelled = true
    }
  }, [requested, pinned, isAdmin, authLoading, setParams])

  if (!manifest || !requested) return null

  async function confirm() {
    if (!requested) return
    try {
      // Leaves the param in the URL on purpose: applyManifest saves the current
      // path as the post-restart destination, and the branch above strips it
      // once the channel is pinned.
      await loadPreviewChannel(requested)
    } catch (err) {
      log('ota', 'preview load failed', { channel: requested, error: String(err) })
      toastError(
        fbt("Couldn't load that build", 'Toast title when installing a preview build fails'),
        classifyError(err),
      )
      setManifest(null)
    }
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={open => {
        if (open) return
        setManifest(null)
        setParams(
          prev => {
            prev.delete(PREVIEW_PARAM)
            return prev
          },
          { replace: true },
        )
      }}
      title={<fbt desc="Title of the dialog asking whether to install a preview build">Load preview build?</fbt>}
      description={
        <span className="space-y-2 block">
          <span className="block font-medium text-foreground">{manifest.branch ?? requested}</span>
          {manifest.subject && <span className="block">{manifest.subject}</span>}
          <span className="block font-mono text-xs">
            {requested}
            {manifest.commit ? ` · ${manifest.commit}` : ''} · {manifest.version}
          </span>
          <span className="block">
            <fbt desc="Warning shown before loading an unreleased preview build into the app">
              This replaces the app with an unreleased build that reads and writes real data. The
              header shows a preview badge until you switch back.
            </fbt>
          </span>
        </span>
      }
      confirmLabel={<fbt desc="Button label to install a preview build into the app">Load build</fbt>}
      onConfirm={() => void confirm()}
    />
  )
}
